import { useEffect, useState } from "react";
import { Mail, Phone, MessageSquare, Trash2, CheckCircle2, Archive, Download, Search, X, Inbox as InboxIcon } from "lucide-react";
import { apiAuthGet, apiAuthSend } from "@/lib/api";
import { Btn } from "./_ui";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    new: { label: "Neu", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    read: { label: "Gelesen", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
    archived: { label: "Archiviert", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  };
  const s = map[status] || map.new;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border ${s.cls}`}>{s.label}</span>;
};

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => apiAuthGet("/admin/contacts").then(setItems).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusy(true);
    try { await apiAuthSend("PATCH", `/admin/contacts/${id}`, { status }); await load(); if (open?.id === id) setOpen({ ...open, status }); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    setBusy(true);
    try { await apiAuthSend("DELETE", `/admin/contacts/${id}`); await load(); if (open?.id === id) setOpen(null); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    const head = ["Datum", "Name", "E-Mail", "Telefon", "Status", "Nachricht"];
    const rows = items.map((c) => [
      fmtDate(c.created_at), c.name, c.email, c.phone || "", c.status,
      (c.message || "").replace(/\r?\n/g, " ").replace(/"/g, '""'),
    ]);
    const csv = [head, ...rows].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kontaktanfragen_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const filtered = items.filter((c) => {
    if (filter !== "all" && c.status !== filter) return false;
    if (q && !`${c.name} ${c.email} ${c.message}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const counts = {
    all: items.length,
    new: items.filter((c) => c.status === "new").length,
    read: items.filter((c) => c.status === "read").length,
    archived: items.filter((c) => c.status === "archived").length,
  };

  return (
    <div className="space-y-6" data-testid="admin-inbox">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-1">Inbox</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-[Outfit]">Kontaktanfragen</h1>
          <p className="text-slate-400 mt-1.5 text-sm">{counts.all} {counts.all === 1 ? "Anfrage" : "Anfragen"} insgesamt · {counts.new} neu</p>
        </div>
        <Btn variant="secondary" onClick={exportCsv} data-testid="inbox-export-csv">
          <Download className="w-4 h-4" /> CSV-Export
        </Btn>
      </div>

      {err && <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-300">{err}</div>}

      {/* Filter & Search */}
      <div className="flex flex-wrap gap-2 items-center">
        {[
          { k: "all", l: "Alle" }, { k: "new", l: "Neu" }, { k: "read", l: "Gelesen" }, { k: "archived", l: "Archiviert" },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`inbox-filter-${f.k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
              filter === f.k ? "bg-emerald-500 text-white" : "bg-white/5 text-slate-400 hover:text-white"
            }`}>
            {f.l} <span className="ml-1 opacity-70">{counts[f.k]}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen …"
            data-testid="inbox-search"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-slate-500 focus:border-emerald-400 focus:outline-none" />
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-12 text-center text-slate-400">
          <InboxIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
          Keine Anfragen gefunden.
        </div>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/5 overflow-hidden">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => { setOpen(c); if (c.status === "new") setStatus(c.id, "read"); }}
              data-testid={`inbox-item-${c.id}`}
              className="w-full text-left px-4 sm:px-5 py-4 hover:bg-white/5 transition flex items-start gap-3 sm:gap-4">
              <div className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-sm shadow">
                {(c.name || "?").substring(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-white truncate">{c.name}</span>
                  <StatusBadge status={c.status} />
                  {c.email_sent && <span className="text-[10px] text-emerald-400 font-bold">✓ E-Mail</span>}
                </div>
                <div className="text-xs text-slate-400 mb-1 truncate">{c.email} · {fmtDate(c.created_at)}</div>
                <div className="text-sm text-slate-300 line-clamp-1 break-words">{c.message}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="inbox-detail">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(null)} />
          <div className="relative w-full sm:max-w-lg h-full bg-slate-900 border-l border-white/10 overflow-y-auto overflow-x-hidden">
            <div className="sticky top-0 flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/10 bg-slate-900 z-10 gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-white font-[Outfit] truncate">Anfrage</h3>
                <StatusBadge status={open.status} />
              </div>
              <button onClick={() => setOpen(null)} className="shrink-0 w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-300">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Name</div>
                <div className="text-white font-semibold">{open.name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">E-Mail</div>
                <a href={`mailto:${open.email}`} className="text-emerald-300 hover:underline break-all">{open.email}</a>
              </div>
              {open.phone && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Telefon</div>
                  <a href={`tel:${open.phone}`} className="text-emerald-300 hover:underline">{open.phone}</a>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Eingegangen</div>
                <div className="text-slate-300 text-sm">{fmtDate(open.created_at)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Nachricht</div>
                <div className="rounded-xl p-4 bg-white/5 border border-white/10 text-slate-200 whitespace-pre-wrap leading-relaxed">{open.message}</div>
              </div>

              {/* Quick Replies */}
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Direkt antworten</div>
                <div className="grid grid-cols-2 gap-2">
                  <a href={`mailto:${open.email}?subject=Re:%20Deine%20Anfrage%20bei%20Thermomix%20Krefeld`}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-sm font-semibold transition">
                    <Mail className="w-4 h-4" /> E-Mail
                  </a>
                  {open.phone && (
                    <a href={`https://wa.me/${open.phone.replace(/[^0-9]/g, "").replace(/^0/, "49")}?text=${encodeURIComponent("Hallo " + open.name + ", danke für deine Anfrage bei Thermomix® Krefeld!")}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-green-500/10 hover:bg-green-500/20 text-green-300 border border-green-500/30 text-sm font-semibold transition">
                      <MessageSquare className="w-4 h-4" /> WhatsApp
                    </a>
                  )}
                  {open.phone && (
                    <a href={`tel:${open.phone}`}
                      className="col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 text-sm font-semibold transition">
                      <Phone className="w-4 h-4" /> {open.phone} anrufen
                    </a>
                  )}
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 flex flex-wrap gap-2">
                {open.status !== "archived" && (
                  <Btn variant="secondary" disabled={busy} onClick={() => setStatus(open.id, "archived")} data-testid="inbox-archive">
                    <Archive className="w-4 h-4" /> Archivieren
                  </Btn>
                )}
                {open.status === "archived" && (
                  <Btn variant="secondary" disabled={busy} onClick={() => setStatus(open.id, "read")}>
                    <CheckCircle2 className="w-4 h-4" /> Wiederherstellen
                  </Btn>
                )}
                <Btn variant="danger" disabled={busy} onClick={() => remove(open.id)} data-testid="inbox-delete">
                  <Trash2 className="w-4 h-4" /> Löschen
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
